import { register } from "swiper/element/bundle";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import { GlobalFaqSchema } from "../ui/Common/FaqSchema/FaqSchema";
import "../styles/global.scss";
import "@algolia/autocomplete-theme-classic/dist/theme.min.css";
import "../ui/SearchOverlay/AutocompleteSearch.scss";
import { appWithTranslation } from "next-i18next";
import { useHashScroll } from "../hooks/useHashScroll";
import { AutocompleteProvider } from "../contexts/AutocompleteContext";
import { resolveSlug } from "../lib/utils/resolveSlug";
import { AlgoliaSearchHit } from "../lib/types/algolia";
import { isInvestorSite } from "../lib/utils/isInvestorSite";

// Dynamically import AutocompleteSearch with SSR disabled
// This prevents "document is not defined" errors during server-side rendering
const AutocompleteSearch = dynamic(
  () => import("../ui/SearchOverlay/AutocompleteSearch").then((mod) => mod.AutocompleteSearch),
  { ssr: false }
);

register();

function MyApp({ Component, pageProps }: AppProps) {
  useHashScroll(40);
  const router = useRouter();
  const [siteSection, setSiteSection] = useState<'main' | 'investor'>('main');

  useEffect(() => {
    setSiteSection(isInvestorSite() ? 'investor' : 'main');
  }, [router.asPath]);

  const handleResultClick = (hit: AlgoliaSearchHit) => {
    const url = resolveSlug(hit.slug);
    router.push(url);
  };

  const handleViewAllClick = (query: string) => {
    const currentLocale = router.locale || 'en';

    // Special case: Swedish investor site uses /sv/sok
    if (currentLocale === 'sv' && siteSection === 'investor') {
      router.push(`/sv/sok?q=${encodeURIComponent(query)}`);
      return;
    }

    // All other cases use /search
    const searchPath = currentLocale === 'en'
      ? '/search'
      : `/${currentLocale}/search`;
    router.push(`${searchPath}?q=${encodeURIComponent(query)}`);
  };

  return (
    <AutocompleteProvider>
      <GlobalFaqSchema />
      <Component {...pageProps} />
      {/* Autocomplete mounted permanently but hidden until triggered */}
      <AutocompleteSearch
        locale={router.locale || 'en'}
        section={siteSection}
        onResultClick={handleResultClick}
        onViewAllClick={handleViewAllClick}
      />
    </AutocompleteProvider>
  );
}
export default appWithTranslation(MyApp);
